import {
  Search,
  Star,
  Clock,
  FolderOpen,
  HardDrive,
  Cloud,
  Volume2,
  Square,
  Share2,
  MessageSquare,
  FileText,
  Pencil,
  Download,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CATEGORY_COLORS = {
  "Negócios": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Projetos": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Conteúdo": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Pessoal": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

export function IdeasPanel({
  ideas,
  filteredIdeas,
  ideasLoading,
  searchQuery,
  setSearchQuery,
  filterCategory,
  setFilterCategory,
  filterFavorites,
  setFilterFavorites,
  CATEGORIES,
  isLocal,
  isAuthenticated,
  playingIdeaId,
  deleteConfirmId,
  setDeleteConfirmId,
  onPlay,
  onShare,
  onLoad,
  onEdit,
  onDelete,
  onFavorite,
  onExportTxt,
  onExportPdf,
  onSend,
  inSplit = false,
  formatDate,
}) {
  return (
    <div className={inSplit ? "flex flex-col h-full" : ""}>
      <div className={inSplit ? "px-4 pt-4 pb-2" : ""}>
        {inSplit && (
          <div className="flex items-center justify-between mb-2">
            <h2
              className="font-heading text-base font-semibold flex items-center gap-2"
              style={{ color: "var(--vi-text)" }}
            >
              Minhas Ideias
              {isLocal ? (
                <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                  <HardDrive className="w-3 h-3" /> Local
                </span>
              ) : (
                <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                  <Cloud className="w-3 h-3" /> Sincronizado
                </span>
              )}
            </h2>
            <span
              className="text-xs"
              style={{ color: "var(--vi-text-muted)" }}
            >
              {ideas.length === 0
                ? "Nenhuma ideia"
                : searchQuery.trim() || filterCategory
                ? `${filteredIdeas.length} de ${ideas.length}`
                : `${ideas.length} ${ideas.length === 1 ? "ideia" : "ideias"}`}
            </span>
          </div>
        )}
        {/* Search */}
        {ideas.length > 0 && (
          <div className={`relative ${inSplit ? "" : "mt-4"}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar ideias..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                background: "var(--vi-input-bg)",
                borderColor: "var(--vi-border-accent)",
                color: "var(--vi-text)",
              }}
              className="pl-10"
              data-testid="search-ideas-input"
            />
          </div>
        )}
        {/* Category Filter + Favorites */}
        {ideas.length > 0 && (
          <div className="mt-3 flex gap-2">
            <Select
              value={filterCategory || "all"}
              onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}
            >
              <SelectTrigger
                className="bg-white/5 border-white/10 flex-1"
                data-testid="filter-category-select"
              >
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {inSplit ? "Todas" : "Todas as categorias"}
                </SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setFilterFavorites(!filterFavorites)}
              className={`shrink-0 h-10 w-10 border ${
                filterFavorites
                  ? "bg-amber-500/20 border-amber-500/30 text-amber-400"
                  : "bg-white/5 border-white/10 text-white/40 hover:text-amber-400"
              }`}
              data-testid="filter-favorites-button"
            >
              <Star
                className={`w-4 h-4 ${filterFavorites ? "fill-amber-400" : ""}`}
              />
            </Button>
          </div>
        )}
      </div>
      {/* Ideas list */}
      <div
        className={`mt-3 space-y-3 overflow-y-auto pr-2 ${
          inSplit ? "flex-1 px-4 pb-4" : ""
        }`}
        style={
          inSplit
            ? { maxHeight: "calc(100vh - 200px)" }
            : { maxHeight: "calc(100vh - 250px)" }
        }
        data-testid="ideas-list"
      >
        {ideasLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <>
            {filteredIdeas.map((idea) => (
              <div
                key={idea.id}
                className="group relative p-4 rounded-xl transition-all duration-200"
                style={{
                  background: "var(--vi-input-bg)",
                  border: "1px solid var(--vi-border-accent)",
                }}
                data-testid={`idea-item-${idea.id}`}
              >
                {deleteConfirmId === idea.id ? (
                  <div className="flex flex-col gap-3">
                    <p className="text-sm" style={{ color: "var(--vi-text-soft)" }}>
                      Excluir esta ideia?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(idea.id);
                        }}
                        className="flex-1"
                        data-testid={`confirm-delete-${idea.id}`}
                      >
                        Excluir
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(null);
                        }}
                        className="flex-1 border-white/10"
                        data-testid={`cancel-delete-${idea.id}`}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-medium truncate"
                          style={{ color: "var(--vi-text)" }}
                          data-testid={`idea-title-${idea.id}`}
                        >
                          {idea.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {idea.category && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full border ${
                                CATEGORY_COLORS[idea.category] ||
                                CATEGORY_COLORS["Pessoal"]
                              }`}
                            >
                              {idea.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDate(idea.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFavorite(idea.id);
                          }}
                          className={`h-8 w-8 transition-all ${
                            idea.favorite
                              ? "text-amber-400 opacity-100"
                              : "text-white/30 opacity-0 group-hover:opacity-100 hover:text-amber-400"
                          }`}
                          data-testid={`favorite-idea-${idea.id}`}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              idea.favorite ? "fill-amber-400" : ""
                            }`}
                          />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onEdit(idea)}
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-indigo-500/20 hover:text-indigo-400 transition-all"
                          data-testid={`edit-idea-${idea.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-cyan-500/20 hover:text-cyan-400 transition-all"
                              data-testid={`export-idea-${idea.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-40"
                            style={{
                              background: "var(--vi-card-solid)",
                              border: "1px solid var(--vi-border-accent)",
                            }}
                          >
                            <DropdownMenuItem
                              onClick={() => onExportPdf(idea)}
                              className="cursor-pointer"
                              data-testid={`export-pdf-${idea.id}`}
                            >
                              <FileText className="w-4 h-4 mr-2 text-rose-400" />
                              Exportar PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onExportTxt(idea)}
                              className="cursor-pointer"
                              data-testid={`export-txt-${idea.id}`}
                            >
                              <FileText className="w-4 h-4 mr-2 text-cyan-400" />
                              Exportar TXT
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteConfirmId(idea.id)}
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-rose-500/20 hover:text-rose-500 transition-all"
                          data-testid={`delete-idea-${idea.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p
                      className="mt-2 text-sm line-clamp-2"
                      style={{ color: "var(--vi-text-muted)" }}
                    >
                      {idea.content}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlay(idea);
                        }}
                        className={`flex-1 justify-center gap-2 transition-all ${
                          playingIdeaId === idea.id
                            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                            : "hover:bg-emerald-500/20 hover:text-emerald-400"
                        }`}
                        style={
                          playingIdeaId !== idea.id
                            ? { background: "var(--vi-input-bg)" }
                            : {}
                        }
                        data-testid={`play-idea-${idea.id}`}
                      >
                        {playingIdeaId === idea.id ? (
                          <>
                            <Square className="w-3.5 h-3.5" />
                            Parar
                          </>
                        ) : (
                          <>
                            <Volume2 className="w-3.5 h-3.5" />
                            Ouvir
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onShare(idea)}
                        className="flex-1 justify-center gap-2 hover:bg-violet-500/20 hover:text-violet-400"
                        style={{ background: "var(--vi-input-bg)" }}
                        data-testid={`share-idea-${idea.id}`}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Compartilhar
                      </Button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {isAuthenticated && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onSend(idea.id, idea.title)}
                          className="flex-1 justify-center gap-2 hover:bg-cyan-500/20 hover:text-cyan-400"
                          style={{ background: "var(--vi-input-bg)" }}
                          data-testid={`send-idea-${idea.id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          Enviar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onLoad(idea)}
                        className="flex-1 justify-center gap-2 hover:bg-indigo-500/20 hover:text-indigo-400"
                        style={{ background: "var(--vi-input-bg)" }}
                        data-testid={`load-idea-${idea.id}`}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Carregar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        )}
        {!ideasLoading && filteredIdeas.length === 0 && (
          <div
            className="text-center py-12"
            data-testid="ideas-empty-state"
          >
            <FolderOpen className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--vi-text-muted)", opacity: 0.3 }} />
            <p className="text-sm" style={{ color: "var(--vi-text-muted)" }}>
              {searchQuery.trim()
                ? "Nenhuma ideia encontrada para esta busca."
                : filterFavorites
                ? "Nenhuma ideia favorita"
                : filterCategory
                ? "Nenhuma ideia nesta categoria"
                : "Suas ideias salvas aparecerão aqui"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
